import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Button} from "primereact/button";
import {Chart} from "primereact/chart";
import {Dropdown} from "primereact/dropdown";
import {InputTextarea} from "primereact/inputtextarea";
import {Message} from "primereact/message";
import {ProgressBar} from "primereact/progressbar";
import {ProgressSpinner} from "primereact/progressspinner";
import {Sidebar} from "primereact/sidebar";
import {Tag} from "primereact/tag";
import {Toast} from "primereact/toast";
import {useLocation} from "react-router-dom";
import {executeAiProposal} from "../../models/ai/ai-action";
import {useAi} from "../../models/ai/use-ai";
import {resolveAiPageContext} from "../ai-page-context";
import {useActiveAiScope} from "../ai-scope-context";
import {usePermission} from "../usePermission";
import {useTranslate} from "../useTranslate";
import {dateToPersian} from "../utils";
import AiMarkdownMessage from "./ai-markdown-message";
import AiActionWizard from "./ai-action-wizard";
import {aiActionLabel} from "./ai-action-label";

const HISTORY_LIMIT = 100;

const getSidebarViewport = () => {
    if (typeof window === "undefined") return "desktop";
    if (window.matchMedia("(max-width: 48rem)").matches) return "mobile";
    if (window.matchMedia("(max-width: 64rem)").matches) return "tablet";
    return "desktop";
};

const useSidebarViewport = () => {
    const [viewport, setViewport] = useState(getSidebarViewport);

    useEffect(() => {
        const mobile = window.matchMedia("(max-width: 48rem)");
        const tablet = window.matchMedia("(max-width: 64rem)");
        const updateViewport = () => setViewport(getSidebarViewport());

        mobile.addEventListener("change", updateViewport);
        tablet.addEventListener("change", updateViewport);

        return () => {
            mobile.removeEventListener("change", updateViewport);
            tablet.removeEventListener("change", updateViewport);
        };
    }, []);

    return viewport;
};

const formatDate = (value) => {
    if (!value) return "—";
    try {
        return dateToPersian(value, "YYYY/MM/DD HH:mm");
    } catch {
        return "—";
    }
};

const confidencePercent = (value) => `${Math.round(Number(value || 0) * 100)}%`;

const mapMessages = (items) => (items || [])
    .filter((item) => ["user", "assistant"].includes(item.role))
    .map((item) => ({
        role: item.role,
        text: item.content || "",
        messageDocumentId: item.documentId,
        confidence: item.confidence,
        model: item.modelKey,
        createdAt: item.createdAt,
        actions: Array.isArray(item.context?.actions) ? item.context.actions : [],
    }));

const conversationScope = (entityDocumentId, entityType, pageType) => ({
    attachedEntityType: entityDocumentId ? entityType : pageType,
    attachedEntityDocumentId: entityDocumentId || undefined,
});

function EmptyState({children}) {
    return <div className="surface-50 border-round p-3 text-xs text-500 text-center line-height-3">{children}</div>;
}

function RecordHeader({title, status, date, translate}) {
    return (
        <div className="flex align-items-start justify-content-between gap-2">
            <div className="flex flex-column gap-1 min-w-0">
                <span className="font-semibold text-xs text-800 line-height-3">{translate(title)}</span>
                <span className="text-xs text-500">{formatDate(date)}</span>
            </div>
            {status && <Tag className="flex-none text-xs" value={translate(`ai.status.${status}`)}/>}
        </div>
    );
}

function EvidenceDetails({evidence, translate}) {
    const items = [
        ...(evidence?.topTasks || []),
        ...(evidence?.items || []),
        ...(evidence?.quickWins || []),
        ...(evidence?.unassignedOpenTasks || []),
    ].filter((item, index, all) => (
        all.findIndex((candidate) => (
            (candidate.documentId || candidate.title || candidate.name)
            === (item.documentId || item.title || item.name)
        )) === index
    )).slice(0, 5);

    if (!items.length) return null;
    return (
        <div className="surface-50 border-round p-2 flex flex-column gap-2">
            <span className="font-semibold text-xs">{translate("ai.action.evidence")}</span>
            {items.map((item) => (
                <div
                    className="border-bottom-1 border-200 pb-2 flex flex-column gap-1 text-xs"
                    key={item.documentId || item.title || item.name}
                >
                    <div className="flex flex-wrap align-items-center gap-2">
                        <span className="font-medium">{item.title || item.name}</span>
                        {(item.taskStatus || item.status) && (
                            <Tag
                                className="text-xs"
                                value={translate(`ai.status.${item.taskStatus || item.status}`)}
                            />
                        )}
                    </div>
                    <div className="flex flex-wrap gap-2 text-500">
                        {item.projectTitle && (
                            <span>{translate("ai.evidence.project")}: {item.projectTitle}</span>
                        )}
                        {item.assignee && (
                            <span>{translate("ai.action.assignee")}: {item.assignee}</span>
                        )}
                        {item.dueDate && (
                            <span>{translate("ai.scope.dueDate")}: {formatDate(item.dueDate)}</span>
                        )}
                        {Number(item.daysOverdue) > 0 && (
                            <span>{translate("ai.evidence.daysOverdue")}: {item.daysOverdue}</span>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}

function OverviewPanel({data, translate}) {
    const overview = data?.workspaceOverview;
    const report = data?.scopeReport;
    const counts = overview?.counts || {};
    const breakdown = report?.statusBreakdown || {};
    const sectionName = translate(`ai.section.${report?.entityType || "workspace"}`);
    const countItems = [
        ["goals", "pi pi-flag"],
        ["projects", "pi pi-folder"],
        ["tasks", "pi pi-check-circle"],
        ["deliverables", "pi pi-box"],
        ["teams", "pi pi-users"],
        ["members", "pi pi-user"],
    ];

    if (!overview) return <EmptyState>{translate("ai.intelligence.noData")}</EmptyState>;

    const statusEntries = Object.entries(breakdown);
    const chartData = {
        labels: statusEntries.map(([status]) => translate(`ai.status.${status}`)),
        datasets: [{
            data: statusEntries.map(([, count]) => count),
            backgroundColor: ["#6366f1", "#22c55e", "#f59e0b", "#ef4444", "#06b6d4", "#8b5cf6", "#64748b"],
            borderWidth: 0,
        }],
    };
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "62%",
        radius: "72%",
        layout: {
            padding: {top: 4, right: 8, bottom: 0, left: 8},
        },
        plugins: {
            legend: {
                position: "bottom",
                rtl: true,
                labels: {
                    boxWidth: 10,
                    boxHeight: 10,
                    padding: 12,
                    font: {size: 10},
                },
            },
        },
    };
    const summaryItems = [
        ["total", report?.total || 0, "pi pi-list", "text-primary"],
        ["completed", report?.completed || 0, "pi pi-check-circle", "text-green-500"],
        ["atRisk", report?.atRisk || 0, "pi pi-exclamation-triangle", "text-orange-500"],
        ["overdue", report?.overdue || 0, "pi pi-clock", "text-red-500"],
    ];

    return (
        <div className="flex flex-column gap-3">
            <div className="surface-50 border-round p-3 flex flex-column gap-2">
                <div className="flex align-items-center justify-content-between gap-2">
                    <span className="font-semibold text-xs">{data?.scope?.entity?.title || overview.workspace?.name}</span>
                    <Tag icon="pi pi-filter" value={translate(`ai.context.${data?.scope?.pageType}`)}/>
                </div>
                {overview.workspace?.mission && <span className="text-xs text-600 line-height-3">{overview.workspace.mission}</span>}
                <span className="text-xs text-500">{translate("ai.intelligence.generatedAt")}: {formatDate(overview.generatedAt)}</span>
            </div>
            {report && (
                <>
                    <div className="grid m-0">
                        {summaryItems.map(([key, value, icon, color]) => (
                            <div className="col-6 p-1" key={key}>
                                <div className="border-1 border-200 border-round p-3 flex align-items-center gap-2 h-full">
                                    <i className={`${icon} ${color}`}/>
                                    <div className="flex flex-column gap-1">
                                        <span className="font-bold text-sm">{value}</span>
                                        <span className="text-xs text-500">
                                            {key === "total"
                                                ? `${translate(`ai.scope.${key}`)} ${sectionName}`
                                                : translate(`ai.scope.${key}`)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="border-1 border-200 border-round p-3 flex flex-column gap-2">
                        <div className="flex align-items-center justify-content-between gap-2">
                            <span className="font-semibold text-xs">{translate("ai.scope.overallProgress")} {sectionName}</span>
                            <span className="font-bold text-xs text-primary">{report?.progress || 0}%</span>
                        </div>
                        <ProgressBar value={report?.progress || 0} showValue={false} className="h-1rem"/>
                    </div>
                    {statusEntries.length > 0 && (
                        <div className="border-1 border-200 border-round p-3 flex flex-column gap-2">
                            <span className="font-semibold text-xs">{translate("ai.scope.statusDistribution")}</span>
                            <div className="ai-status-chart">
                                <Chart type="doughnut" data={chartData} options={chartOptions}/>
                            </div>
                        </div>
                    )}
                    <div className="border-1 border-200 border-round overflow-hidden">
                        <div className="p-3 border-bottom-1 border-200 font-semibold text-xs">
                            <i className="pi pi-table ml-2"/>
                            {translate("ai.scope.itemsTable")} {sectionName}
                        </div>
                        <div className="overflow-auto">
                            <table className="ai-scope-table">
                                <thead>
                                    <tr>
                                        <th>{translate("ai.scope.item")}</th>
                                        <th>{translate("ai.scope.status")}</th>
                                        <th>{translate("ai.scope.progress")}</th>
                                        <th>{translate("ai.scope.dueDate")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(report?.rows || []).map((row) => (
                                        <tr key={row.documentId || row.title}>
                                            <td>{row.title}</td>
                                            <td><Tag value={translate(`ai.status.${row.status}`)}/></td>
                                            <td className="min-w-8rem">
                                                <div className="flex align-items-center gap-2">
                                                    <ProgressBar value={row.progress} showValue={false} className="flex-1 h-1rem"/>
                                                    <span>{row.progress}%</span>
                                                </div>
                                            </td>
                                            <td className="white-space-nowrap">{formatDate(row.dueDate)}</td>
                                        </tr>
                                    ))}
                                    {!report?.rows?.length && (
                                        <tr><td colSpan={4} className="text-center text-500">{translate("ai.intelligence.noData")}</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
            <div className="grid m-0">
                {countItems.map(([key, icon]) => (
                    <div className="col-6 md:col-4 p-1" key={key}>
                        <div className="border-1 border-200 border-round p-3 h-full flex align-items-center gap-2">
                            <i className={`${icon} text-primary`}/>
                            <div className="flex flex-column gap-1">
                                <span className="font-bold text-sm">{counts[key] || 0}</span>
                                <span className="text-xs text-500">{translate(`ai.intelligence.${key}`)}</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function AnalysisHistory({items, translate}) {
    if (!items.length) return <EmptyState>{translate("ai.intelligence.noHistory")}</EmptyState>;
    return (
        <div className="flex flex-column gap-2">
            {items.map((item) => (
                <div className="border-1 border-200 border-round p-3 flex flex-column gap-2" key={item.documentId || item.id}>
                    <RecordHeader title={item.title} status={item.insightStatus} date={item.freshness || item.createdAt} translate={translate}/>
                    <AiMarkdownMessage content={translate(item.summary)}/>
                    <EvidenceDetails evidence={item.evidence} translate={translate}/>
                    <div className="flex flex-wrap gap-2">
                        <Tag severity={item.severity === "critical" ? "danger" : item.severity === "warning" ? "warning" : "info"} value={translate(`ai.severity.${item.severity}`)}/>
                        <Tag value={`${translate("ai.confidence")}: ${confidencePercent(item.confidence)}`}/>
                    </div>
                </div>
            ))}
        </div>
    );
}

function ForecastHistory({items, translate}) {
    if (!items.length) return <EmptyState>{translate("ai.intelligence.noHistory")}</EmptyState>;
    return (
        <div className="flex flex-column gap-2">
            {items.map((item) => (
                <div className="border-1 border-200 border-round p-3 flex flex-column gap-2" key={item.documentId || item.id}>
                    <RecordHeader
                        title={`ai.forecast.${item.forecastType}`}
                        status={item.forecastStatus}
                        date={item.generatedAt || item.createdAt}
                        translate={translate}
                    />
                    {item.drivers?.projectTitle && <span className="text-xs text-700">{item.drivers.projectTitle}</span>}
                    {item.drivers && (
                        <div className="grid m-0">
                            {[
                                ["totalTasks", "ai.evidence.totalTasks"],
                                ["openTasks", "ai.evidence.openTasks"],
                                ["completedTasks", "ai.evidence.completedTasks"],
                                ["overdueTasks", "ai.evidence.overdueTasks"],
                                ["blockedTasks", "ai.evidence.blockedTasks"],
                            ].filter(([key]) => item.drivers[key] !== undefined).map(([key, label]) => (
                                <div className="col-6 p-1" key={key}>
                                    <div className="surface-50 border-round p-2 flex flex-column gap-1">
                                        <span className="text-xs text-500">{translate(label)}</span>
                                        <span className="text-xs font-semibold">{item.drivers[key]}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                        <Tag value={`${translate("ai.probability")}: ${confidencePercent(item.probability)}`}/>
                        <Tag value={`${translate("ai.confidence")}: ${confidencePercent(item.confidence)}`}/>
                        <Tag icon="pi pi-calendar" value={`${translate("ai.intelligence.likelyCompletion")}: ${formatDate(item.likely?.completionDate)}`}/>
                    </div>
                </div>
            ))}
        </div>
    );
}

function RecommendationHistory({items, translate}) {
    if (!items.length) return <EmptyState>{translate("ai.intelligence.noHistory")}</EmptyState>;
    return (
        <div className="flex flex-column gap-2">
            {items.map((item) => (
                <div className="border-1 border-200 border-round p-3 flex flex-column gap-2" key={item.documentId || item.id}>
                    <RecordHeader title={item.title} status={item.recommendationStatus} date={item.generatedAt || item.createdAt} translate={translate}/>
                    <AiMarkdownMessage content={translate(item.summary)}/>
                    {item.reasoning && (
                        <div className="surface-50 border-round p-2 text-xs line-height-3">
                            <span className="font-semibold">{translate("ai.details.reasoning")}: </span>
                            {translate(item.reasoning)}
                        </div>
                    )}
                    <EvidenceDetails evidence={item.evidence} translate={translate}/>
                    <Tag className="align-self-start" value={`${translate("ai.confidence")}: ${confidencePercent(item.confidence)}`}/>
                </div>
            ))}
        </div>
    );
}

function ActionSuggestions({items, translate, onSelect}) {
    if (!items.length) return <EmptyState>{translate("ai.action.none")}</EmptyState>;
    return (
        <div className="flex flex-column gap-2">
            {items.map((item) => (
                <div className="border-1 border-200 border-round p-3 flex flex-column gap-2" key={item.id}>
                    <div className="flex align-items-start justify-content-between gap-2">
                        <div className="flex align-items-start gap-2 min-w-0">
                            <i className="pi pi-sparkles text-primary mt-1"/>
                            <div className="flex flex-column gap-1 min-w-0">
                                <span className="font-semibold text-xs line-height-3">{item.title}</span>
                                <span className="text-xs text-600 line-height-3">{item.summary}</span>
                            </div>
                        </div>
                        <Tag
                            className="text-xs flex-none"
                            value={`${translate("ai.confidence")}: ${confidencePercent(item.confidence)}`}
                        />
                    </div>
                    {item.relatedItems?.length > 0 && (
                        <div className="flex flex-column gap-1 surface-50 border-round p-2">
                            <span className="text-xs font-semibold">{translate("ai.action.evidence")}</span>
                            {item.relatedItems.slice(0, 4).map((relatedItem) => (
                                <div
                                    className="flex flex-wrap align-items-center gap-2 text-xs"
                                    key={relatedItem.documentId}
                                >
                                    <span className="font-medium">{relatedItem.title}</span>
                                    {relatedItem.status && (
                                        <Tag
                                            className="text-xs"
                                            value={translate(`ai.status.${relatedItem.status}`)}
                                        />
                                    )}
                                    {relatedItem.dueDate && (
                                        <span className="text-500">
                                            {translate("ai.scope.dueDate")}: {formatDate(relatedItem.dueDate)}
                                        </span>
                                    )}
                                    {relatedItem.assignee && (
                                        <span className="text-500">
                                            {translate("ai.action.assignee")}: {relatedItem.assignee}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    <Button
                        type="button"
                        className="text-xs gap-2 align-self-start"
                        icon="pi pi-bolt"
                        label={aiActionLabel(item, translate)}
                        onClick={() => onSelect(item)}
                    />
                </div>
            ))}
        </div>
    );
}

function ReportSection({icon, title, children}) {
    return (
        <section className="flex flex-column gap-2">
            <div className="flex align-items-center gap-2 border-bottom-1 border-200 pb-2">
                <i className={`${icon} text-primary`}/>
                <span className="font-semibold text-xs">{title}</span>
            </div>
            {children}
        </section>
    );
}

function AiAssistantSidebar({visible, setVisible}) {
    const translate = useTranslate();
    const sidebarViewport = useSidebarViewport();
    const {ai} = useAi();
    const permission = usePermission();
    const location = useLocation();
    const baseContext = useMemo(() => resolveAiPageContext(location.pathname), [location.pathname]);
    const context = useActiveAiScope(baseContext);
    const {entityDocumentId, entityType, pageType} = context;
    const chatScope = useMemo(
        () => conversationScope(entityDocumentId, entityType, pageType),
        [entityDocumentId, entityType, pageType],
    );
    const [messages, setMessages] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [conversations, setConversations] = useState([]);
    const [activeConversationId, setActiveConversationId] = useState(null);
    const [intelligence, setIntelligence] = useState({center: null, insights: [], forecasts: [], recommendations: []});
    const [selectedAction, setSelectedAction] = useState(null);
    const [executingAction, setExecutingAction] = useState(false);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [loadingIntelligence, setLoadingIntelligence] = useState(false);
    const [error, setError] = useState(null);
    const activeRun = useRef(null);
    const messageList = useRef(null);
    const toast = useRef(null);

    const refreshConversationList = useCallback(async () => {
        const items = await ai.conversations({limit: HISTORY_LIMIT, ...chatScope});
        setConversations(items || []);
        return items || [];
    }, [ai, chatScope]);

    const openConversation = useCallback(async (documentId) => {
        setActiveConversationId(documentId || null);
        setError(null);
        if (!documentId) {
            setMessages([]);
            return;
        }
        setLoadingHistory(true);
        try {
            const items = await ai.conversationMessages(documentId, {limit: 200});
            setMessages(mapMessages(items));
        } catch (requestError) {
            setError(requestError);
        } finally {
            setLoadingHistory(false);
        }
    }, [ai]);

    const refreshIntelligence = useCallback(async () => {
        setLoadingIntelligence(true);
        setError(null);
        try {
            const center = await ai.commandCenter({...context, route: location.pathname});
            setIntelligence({
                center,
                insights: center?.insights || [],
                forecasts: center?.forecasts || [],
                recommendations: center?.recommendations || [],
                actionSuggestions: center?.actionSuggestions || [],
            });
        } catch (requestError) {
            setError(requestError);
        } finally {
            setLoadingIntelligence(false);
        }
    }, [ai, context, location.pathname]);

    useEffect(() => {
        if (!visible) return;
        let cancelled = false;
        setError(null);
        setLoadingHistory(true);
        Promise.all([
            ai.suggestions(context.pageType),
            ai.conversations({limit: HISTORY_LIMIT, ...chatScope}),
        ]).then(async ([suggestionResult, conversationItems]) => {
            if (cancelled) return;
            const items = conversationItems || [];
            setSuggestions(suggestionResult?.suggestions || []);
            setConversations(items);
            const selectedId = items[0]?.documentId || null;
            setActiveConversationId(selectedId);
            if (!selectedId) {
                setMessages([]);
                return;
            }
            const persistedMessages = await ai.conversationMessages(selectedId, {limit: 200});
            if (!cancelled) setMessages(mapMessages(persistedMessages));
        }).catch((requestError) => {
            if (!cancelled) setError(requestError);
        }).finally(() => {
            if (!cancelled) setLoadingHistory(false);
        });
        refreshIntelligence();
        return () => {
            cancelled = true;
        };
    }, [ai, chatScope, context.pageType, refreshIntelligence, visible]);

    useEffect(() => {
        const element = messageList.current;
        if (element) element.scrollTop = element.scrollHeight;
    }, [loading, messages]);

    const send = async (suggested) => {
        const text = String(suggested || input).trim();
        if (!text || loading) return;
        setInput("");
        setError(null);
        setMessages((current) => [...current, {role: "user", text}]);
        setLoading(true);
        try {
            const result = await ai.askPage({
                ...context,
                route: location.pathname,
                message: text,
                persist: true,
                title: text.slice(0, 80),
                conversationDocumentId: activeConversationId || undefined,
                ...chatScope,
            });
            activeRun.current = result?.runId || null;
            const conversationId = result?.conversation?.documentId || activeConversationId;
            if (conversationId) setActiveConversationId(conversationId);
            setMessages((current) => [...current, {
                role: "assistant",
                text: result?.text || "",
                messageDocumentId: result?.message?.documentId,
                confidence: result?.confidence,
                model: result?.model,
                actions: result?.actions || [],
            }]);
            await refreshConversationList();
        } catch (requestError) {
            setError(requestError);
        } finally {
            setLoading(false);
            activeRun.current = null;
        }
    };

    const feedback = (messageDocumentId, rating) => {
        if (messageDocumentId) ai.feedback(messageDocumentId, rating).catch(() => undefined);
    };

    const executeAction = async (proposalInput) => {
        setExecutingAction(true);
        setError(null);
        try {
            const execution = await executeAiProposal(
                ai,
                proposalInput,
                translate("ai.action.approvalReason"),
            );
            toast.current?.show({
                severity: execution.status === "executed" ? "success" : "info",
                summary: translate(`ai.action.${execution.status}`),
                detail: translate(`ai.action.${execution.status}Description`),
                life: 5000,
            });
            setSelectedAction(null);
            await refreshIntelligence();
        } catch (requestError) {
            setError(requestError);
            toast.current?.show({
                severity: "error",
                summary: translate("ai.action.failed"),
                detail: translate(requestError?.code || "ai.error.generic"),
                life: 5000,
            });
            throw requestError;
        } finally {
            setExecutingAction(false);
        }
    };

    const generateActionDraft = async ({creationType, description}) => {
        const result = await ai.generateCreationDraft({
            creationType,
            description,
            pageContext: {...context, route: location.pathname},
        });
        return result?.suggestion;
    };

    const selectedConversationTemplate = (option, props) => (
        option
            ? <span className="text-xs">{option.title}</span>
            : <span className="text-xs text-500">{props.placeholder}</span>
    );
    const conversationTemplate = (option) => (
        <div className="flex align-items-center justify-content-between gap-2">
            <div className="flex flex-column gap-1 min-w-0">
                <span className="text-xs overflow-hidden text-overflow-ellipsis white-space-nowrap">{option.title}</span>
                <span className="text-xs text-500">{formatDate(option.lastMessageAt || option.createdAt)}</span>
            </div>
            <Tag className="text-xs flex-none" value={translate(`ai.status.${option.conversationStatus}`)}/>
        </div>
    );
    const lastModel = [...messages].reverse().find((message) => message.role === "assistant" && message.model)?.model;

    return (
        <>
            <Toast ref={toast} position="bottom-left"/>
            <Sidebar
                onHide={() => setVisible?.(false)}
                visible={visible}
                position="left"
                className="rtl"
                header={translate("ai.assistant.title")}
                pt={{
                    root: () => ({
                        style: {
                            width: sidebarViewport === "mobile" ? "100vw" : sidebarViewport === "tablet" ? "min(96vw, 52rem)" : "min(94vw, 76rem)",
                            maxWidth: "100vw",
                            border: 0,
                            boxShadow: "0 0 2rem color-mix(in srgb, var(--text-color) 16%, transparent)",
                        },
                    }),
                    header: () => ({
                        style: {
                            flex: "0 0 auto",
                            minHeight: sidebarViewport === "mobile" ? "3.5rem" : "4rem",
                            padding: 0,
                            margin: 0,
                            borderBottom: "1px solid var(--surface-border)",
                            background: "var(--surface-card)",
                        },
                    }),
                    title: () => ({style: {fontSize: ".875rem", fontWeight: 700}}),
                    content: () => ({
                        style: {
                            height: sidebarViewport === "mobile" ? "calc(100dvh - 3.5rem)" : "calc(100dvh - 4rem)",
                            minHeight: 0,
                            padding: 0,
                            overflow: sidebarViewport === "desktop" ? "hidden" : "auto",
                            background: "var(--surface-ground)",
                        },
                    }),
                }}
            >
                <div className="ai-assistant-columns">
                <section className="ai-assistant-pane ai-assistant-chat-pane">
                    <div className="flex align-items-center justify-content-between gap-2">
                        <div className="flex align-items-center gap-2">
                            <i className="pi pi-comments text-primary"/>
                            <span className="font-semibold text-xs">{translate("ai.assistant.chat")}</span>
                        </div>
                        <div className="flex align-items-center gap-2">
                            <Tag value={translate(`ai.context.${context.pageType}`)} icon="pi pi-sparkles"/>
                            {lastModel && <Tag severity="info" icon="pi pi-sparkles" value={`${translate("ai.assistant.model")}: ${lastModel}`}/>}
                        </div>
                    </div>

                    <div className="flex align-items-center gap-2">
                        <Dropdown
                            className="flex-1 min-w-0 text-xs"
                            value={activeConversationId}
                            options={conversations}
                            optionLabel="title"
                            optionValue="documentId"
                            placeholder={translate("ai.assistant.noConversations")}
                            valueTemplate={selectedConversationTemplate}
                            itemTemplate={conversationTemplate}
                            onChange={(event) => openConversation(event.value)}
                        />
                        <Button
                            type="button"
                            outlined
                            className="text-xs gap-2 flex-none"
                            icon="pi pi-plus"
                            label={translate("ai.assistant.newConversation")}
                            onClick={() => openConversation(null)}
                        />
                    </div>

                    <div ref={messageList} className="flex-1 min-h-0 overflow-auto flex flex-column gap-2 py-2 px-1">
                        {loadingHistory && <div className="flex justify-content-center p-3"><ProgressSpinner className="w-2rem h-2rem"/></div>}
                        {!loadingHistory && !messages.length && <EmptyState>{translate("ai.assistant.welcome")}</EmptyState>}
                        {!loadingHistory && !messages.length && suggestions.map((suggestion) => (
                            <Button
                                key={suggestion}
                                type="button"
                                outlined
                                icon="pi pi-sparkles"
                                className="text-xs gap-2 text-right justify-content-start"
                                label={translate(suggestion)}
                                onClick={() => send(translate(suggestion))}
                            />
                        ))}
                        {messages.map((message, index) => (
                            <div key={message.messageDocumentId || `${message.role}-${index}`} className="flex w-full">
                                <div className={`ai-assistant-message p-3 border-round text-xs line-height-3 ${message.role === "user" ? "ai-assistant-user-message bg-primary text-primary-contrast white-space-pre-wrap" : "ai-assistant-ai-message surface-100 text-800"}`}>
                                    {message.role === "assistant"
                                        ? <AiMarkdownMessage content={message.text}/>
                                        : message.text}
                                    {message.role === "assistant" && message.actions?.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mt-3 pt-2 border-top-1 border-200">
                                            {message.actions.map((action) => (
                                                <Button
                                                    key={action.id}
                                                    type="button"
                                                    outlined
                                                    className="text-xs gap-2"
                                                    icon="pi pi-sparkles"
                                                    label={aiActionLabel(action, translate)}
                                                    onClick={() => setSelectedAction(action)}
                                                />
                                            ))}
                                        </div>
                                    )}
                                    {message.role === "assistant" && (
                                        <div className="flex align-items-center gap-2 mt-2">
                                            <Button text rounded size="small" icon="pi pi-thumbs-up" aria-label={translate("ai.feedback.positive")} onClick={() => feedback(message.messageDocumentId, "positive")}/>
                                            <Button text rounded size="small" icon="pi pi-thumbs-down" aria-label={translate("ai.feedback.negative")} onClick={() => feedback(message.messageDocumentId, "negative")}/>
                                            {message.confidence !== undefined && <span className="text-xs text-500">{translate("ai.confidence")}: {confidencePercent(message.confidence)}</span>}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                        {loading && <span className="text-xs text-500">{translate("ai.assistant.thinking")}</span>}
                    </div>

                    {error && <Message severity="error" text={translate(error.code || "ai.error.generic")}/>}
                    <div className="flex align-items-end gap-2 border-top-1 border-200 pt-2">
                        <InputTextarea
                            autoResize
                            rows={2}
                            className="flex-1 text-xs"
                            value={input}
                            placeholder={translate("ai.assistant.placeholder")}
                            onChange={(event) => setInput(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter" && !event.shiftKey) {
                                    event.preventDefault();
                                    send();
                                }
                            }}
                        />
                        {loading
                            ? <Button icon="pi pi-stop" severity="danger" outlined aria-label={translate("ai.assistant.stop")} onClick={() => activeRun.current && ai.cancel(activeRun.current)}/>
                            : <Button icon="pi pi-send" aria-label={translate("ai.assistant.send")} disabled={!input.trim()} onClick={() => send()}/>}
                    </div>
                </section>

                <section className="ai-assistant-pane ai-assistant-intelligence-pane">
                    <div className="flex align-items-center justify-content-between gap-2">
                        <div className="flex align-items-center gap-2">
                            <i className="pi pi-chart-line text-primary"/>
                            <span className="font-semibold text-xs">{translate("ai.assistant.intelligence")}</span>
                        </div>
                        <Button
                            type="button"
                            text
                            className="text-xs gap-2"
                            icon="pi pi-refresh"
                            label={translate("ai.intelligence.refresh")}
                            loading={loadingIntelligence}
                            onClick={refreshIntelligence}
                        />
                    </div>
                    <div className="surface-50 border-round p-2 text-xs text-500 line-height-3">
                        <i className="pi pi-database ml-2"/>
                        {translate("ai.intelligence.retained")}
                    </div>
                    {loadingIntelligence && !intelligence.center ? (
                        <div className="flex-1 flex align-items-center justify-content-center"><ProgressSpinner/></div>
                    ) : (
                        <div className="ai-assistant-report-stream">
                            <ReportSection icon="pi pi-database" title={translate("ai.intelligence.overview")}>
                                <OverviewPanel data={intelligence.center} translate={translate}/>
                            </ReportSection>
                            <ReportSection icon="pi pi-history" title={translate("ai.intelligence.analysisHistory")}>
                                <AnalysisHistory items={intelligence.insights} translate={translate}/>
                            </ReportSection>
                            <ReportSection icon="pi pi-chart-line" title={translate("ai.intelligence.forecasts")}>
                                <ForecastHistory items={intelligence.forecasts} translate={translate}/>
                            </ReportSection>
                            <ReportSection icon="pi pi-bolt" title={translate("ai.intelligence.recommendations")}>
                                <ActionSuggestions
                                    items={intelligence.actionSuggestions || []}
                                    translate={translate}
                                    onSelect={setSelectedAction}
                                />
                                <RecommendationHistory items={intelligence.recommendations} translate={translate}/>
                            </ReportSection>
                        </div>
                    )}
                </section>
                </div>
            </Sidebar>
            <AiActionWizard
                visible={Boolean(selectedAction)}
                suggestion={selectedAction}
                initialType={selectedAction?.entityType === "goal" ? "goal" : "project"}
                allowedTypes={[
                    ...(permission.can("create-new-projects") ? ["project"] : []),
                    ...(permission.can("create-new-goals") ? ["goal"] : []),
                ]}
                allowedEntityTypes={[
                    ...(permission.can("create-new-projects") ? ["project"] : []),
                    ...(permission.can("canManageTasks") ? ["deliverable", "task"] : []),
                    ...(permission.can("manage-teams") ? ["team"] : []),
                ]}
                loading={executingAction}
                onHide={() => !executingAction && setSelectedAction(null)}
                onGenerate={generateActionDraft}
                onSubmit={executeAction}
                draftContext={{...context, route: location.pathname}}
            />
        </>
    );
}

export default AiAssistantSidebar;
